import { useState, type CSSProperties } from "react";
import { TaskDescriptionEditor } from "@/components/tasks/details/TaskDescriptionEditor/TaskDescriptionEditor";

export type TaskInfoValue = {
  title: string;
  description: string;
};

export type TaskInfoChangeReason = "title" | "description";

export type TaskInfoSectionProps = {
  value: TaskInfoValue;
  savedValue?: TaskInfoValue;
  colors: {
    bar: string;
    soft: string;
    ink: string;
  };
  onChange: (value: TaskInfoValue, reason: TaskInfoChangeReason) => void;
  onCommit?: (value: TaskInfoValue, reason: TaskInfoChangeReason) => void;
  onDisplayClick?: () => void;
};

export default function TaskInfoSection({
  value,
  savedValue = value,
  colors,
  onChange,
  onCommit,
  onDisplayClick,
}: TaskInfoSectionProps) {
  const [editingDescription, setEditingDescription] = useState(false);
  const style = {
    "--task-info-bar": colors.bar,
    "--task-info-soft": colors.soft,
    "--task-info-ink": colors.ink,
  } as CSSProperties;

  function change<K extends keyof TaskInfoValue>(key: K, next: TaskInfoValue[K]) {
    onChange({ ...value, [key]: next }, key);
  }

  return (
    <section className="task-info-section" style={style}>
      <div className="title-row">
        <button
          type="button"
          className="style-button"
          onClick={onDisplayClick}
          aria-label="Edit display colors"
          title="Edit display colors"
        >
          <span className="style-dot" aria-hidden />
        </button>
        <textarea
          className="title-input"
          aria-label="Task title"
          rows={1}
          value={value.title}
          placeholder="Task title"
          onChange={event => change("title", event.target.value)}
          onBlur={() => {
            if (value.title !== savedValue.title) onCommit?.(value, "title");
          }}
          onInput={event => {
            const element = event.currentTarget;
            element.style.height = "auto";
            element.style.height = `${element.scrollHeight}px`;
          }}
        />
      </div>
      <div className="description-field">
        <TaskDescriptionEditor
          value={value.description}
          savedValue={savedValue.description}
          onChange={description => change("description", description)}
          onCommit={description => {
            const next = { ...value, description: description ?? "" };
            onChange(next, "description");
            onCommit?.(next, "description");
          }}
          editDescription={editingDescription}
          onEditDescriptionChange={setEditingDescription}
          alwaysVisible
        />
      </div>
    </section>
  );
}
